import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { filter, map, startWith } from 'rxjs/operators';
import { Crop, CropVarietySpecificNormValue, CropVarietySpecificNorm, CropVariety, SiteState } from 'src/app/shared/models';
import { ClearCropVarieties, LoadCropVarietiesByCropIdRequest } from 'src/app/store/actions';
import { getCropVarieties } from 'src/app/store/selectors';

import { CropVarietySpecificNormDialogModel } from './crop-variety-specific-norm-dialog.model';
import { DisplayService } from '../../../shared/services/display.service';

@Component({
  selector: 'app-crop-variety-specific-norm-dialog',
  templateUrl: './crop-variety-specific-norm-dialog.component.html',
  standalone: false,
})
export class CropVarietySpecificNormDialogComponent extends AbstractFormDialog<CropVarietySpecificNorm> implements OnInit {
  data = inject<CropVarietySpecificNormDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<CropVarietySpecificNormDialogComponent, AbstractDialogResultData<CropVarietySpecificNorm>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  displayService = inject(DisplayService);
  protected store = inject<Store<SiteState>>(Store);

  formGroup = this.fb.group({
    id: [this.data.cropVarietySpecificNorm.id],
    calendarYear: [this.data.cropVarietySpecificNorm.calendarYear, [Validators.required]],
    cropId: [this.data.cropVarietySpecificNorm.cropId, [Validators.required]],
    cropVarietyId: [this.data.cropVarietySpecificNorm.cropVarietyId, [Validators.required]],
    cropVarietySpecificNorm: [this.data.cropVarietySpecificNorm.cropVarietySpecificNorm],
    isNitrogenDifferentiation: [this.data.cropVarietySpecificNorm.isNitrogenDifferentiation],
  });

  cropVarietySpecificNormValues = CropVarietySpecificNormValue;

  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.removeDuplicateCrops([...this.filterCrop(value, [...this.data.crops])])),
  );

  cropId$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(this.data.cropVarietySpecificNorm.cropId),
    filter((value) => typeof value === 'number'),
  );
  cropId: number;

  cropVarieties$ = this.store.pipe(select(getCropVarieties));
  cropVarieties: CropVariety[] = [];

  filteredCropVarieties$ = this.formGroup.controls.cropVarietyId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.removeDuplicateCropVarieties([...this.filterCropVariety(value, [...this.cropVarieties])])),
  );

  ngOnInit(): void {
    this.store.dispatch(new ClearCropVarieties());

    this.cropId$.subscribe((cropId) => {
      // Prevents ExpressionChangedAfterItHasBeenCheckedError when selected field doesn't match filter
      setTimeout(() => this.loadCropVarieties(cropId), 0);
      this.cropId = cropId;
    });

    this.cropVarieties$.subscribe((cropVarieties) => {
      this.cropVarieties = cropVarieties;
      this.formGroup.controls.cropVarietyId.setValue(
        this.cropId === this.data.cropVarietySpecificNorm.cropId ? this.data.cropVarietySpecificNorm.cropVarietyId : 0,
      );
    });
  }

  isNewCropVarietySpecificNorm() {
    return !this.data.cropVarietySpecificNorm.id;
  }

  loadCropVarieties(cropId: number) {
    if (cropId) {
      this.store.dispatch(new LoadCropVarietiesByCropIdRequest(cropId));
    }
  }

  confirm() {
    super.confirm({
      ...this.data.cropVarietySpecificNorm,
      ...this.formGroup.getRawValue(),
      agrifirmCropId: this.getCrop(this.formGroup.get('cropId').value)?.agrifirmCropId,
      cropName: this.getCrop(this.formGroup.get('cropId').value)?.name,
      cropVarietyName: this.getCropVariety(this.formGroup.get('cropVarietyId').value)?.name,
      ediCode: this.getCropVariety(this.formGroup.get('cropVarietyId').value)?.ediCode,
    });
  }

  getCrop = (cropId?: number) => (cropId ? this.data.crops.find((crop) => crop.id === cropId) : null);

  displayCrop() {
    return (cropId?: number) => this.getCrop(cropId)?.name ?? '';
  }

  getCropVariety = (cropVarietyId?: number) => (cropVarietyId ? this.cropVarieties.find((cropVariety) => cropVariety.id === cropVarietyId) : null);

  displayCropVariety() {
    return (cropVarietyId?: number) => this.getCropVariety(cropVarietyId)?.name ?? '';
  }

  getCropVarietySpecificNormString = (key: string): string => this.displayService.getCropVarietySpecificNormString(key);

  private filterCrop(value: string, items: Crop[]) {
    return [...items]
      .sort((a, b) => {
        if (!(a.name && b.name)) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
      .filter((option) => {
        if (!option.name) {
          return false;
        }
        return option.name.toLowerCase().includes(value.toLowerCase());
      });
  }

  private filterCropVariety(value: string, items: CropVariety[]) {
    return [...items]
      .sort((a, b) => {
        if (!(a.name && b.name)) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
      .filter((option) => {
        if (!option.name) {
          return false;
        }
        return option.name.toLowerCase().includes(value.toLowerCase());
      });
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter((v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i);
  }

  private removeDuplicateCropVarieties(items: CropVariety[]): CropVariety[] {
    return items.filter((v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i);
  }
}
